/* eslint-disable max-lines */
import type { PayloadAction } from '@reduxjs/toolkit';
import config from 'Cloud/config';
import { addYears } from 'date-fns';
import { logger } from 'lib/logger';
import { path, pathOr } from 'ramda';
import { getFileInfoApiCall } from 'reactApp/api/FileInfoAPICall';
import { weblinksSetFlagsV4ApiCall } from 'reactApp/api/weblinks/WeblinksSetFlagsApiCall';
import { IS_PUBLIC_ALBUM } from 'reactApp/appHelpers/configHelpers';
import { appendUtmsToPromoLink } from 'reactApp/appHelpers/featuresHelpers/features/promoLinks';
import { DomainAccess, EFrom } from 'reactApp/components/SharingWindow/Sharing.types';
import { NEW_FOLDER_DEFAULT_NAME, ROOT_FOLDER_ID } from 'reactApp/constants/magicIdentificators';
import { albumsLoadMoreRequest } from 'reactApp/modules/albums/albums.actions';
import { allDocumentsMoreRequest } from 'reactApp/modules/allDocuments/allDocuments.module';
import { attachesLoadMoreRequest } from 'reactApp/modules/attaches/attaches.actions';
import { requestMoreFilesWithFaceStart } from 'reactApp/modules/faces/faces.module';
import { hasMoreToLoad } from 'reactApp/modules/faces/faces.selectors';
import { feedLoadMoreRequest } from 'reactApp/modules/feed/feed.module';
import { getFolderParams } from 'reactApp/modules/file/utils';
import { galleryLoadMoreRequest } from 'reactApp/modules/gallery/gallery.module';
import { loadHomeFolderRequest, loadMoreHomeRequest } from 'reactApp/modules/home/home.actions';
import { getHomeItemById } from 'reactApp/modules/home/home.selectors';
import type { HomeFolder } from 'reactApp/modules/home/home.types';
import { getHomeList } from 'reactApp/modules/home/selector/getHomeList';
import { loadMoreIncomingPublicRequest } from 'reactApp/modules/incomingPublic/incomingPublic.module';
import {
    addToFavorites,
    checkFolderForUpdate,
    cloneRequest,
    copyRequest,
    copyRequestEnded,
    createFamilyAlbumAction,
    createFamilyFolderAction,
    createFamilyFolderSuccess,
    createFolderAction,
    createFolderSuccess,
    createNewAutoDeleteFolder,
    createNewFolderAndPublish,
    createNewItem,
    createThemedPublicAndOpen,
    deleteWeblinkExpiresRequest,
    downloadMobileItem,
    editPdfAttachCopy,
    editPdfCopy,
    editPublicCopy,
    forceCheckFolderForUpdate,
    inlineEditFailure,
    inlineEditSuccess,
    loadMoreRequest,
    makeNewDocumentSuccess,
    modifyingStop,
    mountRequest,
    moveRequest,
    openPublishDialog,
    publishAutoDeleteFolder,
    publishRequest,
    publishWeblink,
    removeFileEnded,
    removeFromFavorites,
    removeRequest,
    renameFileRequest,
    silentlyDeleteFamilyFolderOnFailedCreation,
    toggleSeoRequest,
    toggleWeblinkDomestic,
    toggleWeblinkDomesticRequest,
    toggleWeblinkDownloadableRequest,
    toggleWeblinkEditableRequest,
    toggleWeblinkUploadRequest,
    unmountRequest,
    unPublishRequest,
    unPublishWeblink,
    updatePublicFlags,
    updateWeblinkAutoDeleteRequest,
    updateWeblinkCountDownloadsRequest,
    updateWeblinkExpiresRequest,
    uploadFilesEnded,
    weblinkSetDomainAccess,
} from 'reactApp/modules/modifying/modifying.actions';
import {
    type ICreateNewFolderAndPublish,
    type IInlineEditSuccess,
    type IUpdatePublicFlags,
    type IWeblinkSetDomainAccess,
    type TItem,
    EInlineEditPlace,
    EModifyReason,
} from 'reactApp/modules/modifying/modifying.types';
import { handeClonePdfForEdit, handeEditPublicCopy, handleCloneItems } from 'reactApp/modules/modifying/sagas/clone.saga';
import { handleCreateNewItem } from 'reactApp/modules/modifying/sagas/createNewItem.saga';
import {
    deleteExpires,
    handleOpenPublishDialog,
    handlePublishRequest,
    handleUnPublishRequest,
    openPublishDialogSaga,
    publishRequest as publishRequestFn,
    toggleDomestic,
    toggleDownloadable,
    toggleEditable,
    toggleUpload,
    updateAutoDelete,
    updateCount,
    updateExpires,
} from 'reactApp/modules/modifying/sagas/publish.saga';
import { toggleSeo } from 'reactApp/modules/modifying/sagas/toggleSeo';
import { loadDocumentsMoreRequest } from 'reactApp/modules/personalDocuments/personalDocuments.module';
import { loadMorePublicFolderRequest, updateFolderRequest } from 'reactApp/modules/public/public.actions';
import { getPublicItemById, getPublicRootWeblink } from 'reactApp/modules/public/public.selectors';
import type { PublicFolder } from 'reactApp/modules/public/public.types';
import { getCurrentRouteId, getCurrentStorage } from 'reactApp/modules/router/router.selectors';
import { searchLoadMoreRequest } from 'reactApp/modules/search/search.module';
import { scrollToHomeItemAction } from 'reactApp/modules/selections/selections.actions';
import { SettingsSelectors } from 'reactApp/modules/settings/settings.selectors';
import { showSnackbarAction } from 'reactApp/modules/snackbar/snackbar.actions';
import { SnackbarTypes } from 'reactApp/modules/snackbar/snackbar.types';
import { digestRequest } from 'reactApp/modules/start/start.module';
import { loadStockMoreRequest } from 'reactApp/modules/stock/stock.module';
import { getLoadMoreLimit, getStorage } from 'reactApp/modules/storage/storage.helpers';
import { scrollToItemAction } from 'reactApp/modules/storage/storage.module';
import { getStorageCurrentFolder, getStorageItemById } from 'reactApp/modules/storage/storage.selectors';
import { EStorageType } from 'reactApp/modules/storage/storage.types';
import { trashbinLoadMoreRequest } from 'reactApp/modules/trashbin/trashbin.module';
import type { IInputFile } from 'reactApp/modules/uploadList/uploadList.model';
import { updateUploadFilesAction } from 'reactApp/modules/uploadList/uploadList.module';
import { getFolder, getSuccessInputFiles } from 'reactApp/modules/uploadList/uploadList.selectors';
import { loadUserQuotaGroupMore } from 'reactApp/modules/userQuotaCleaner/userQuotaCleaner.actions';
import { Visibly } from 'reactApp/modules/visibly/Visibly';
import { getParams as getAttachesParams } from 'reactApp/sections/AttachesPage/helpers';
import { store } from 'reactApp/store';
import { sendGa, sendXray } from 'reactApp/utils/ga';
import opener from 'reactApp/utils/opener';
import { ECategoryGa, sendPaymentGa } from 'reactApp/utils/paymentGa';
import { getPublicUrl } from 'reactApp/utils/urlHelper';
import { channel } from 'redux-saga';
import { call, cancel, put, take, takeEvery, takeLatest, throttle } from 'redux-saga/effects';
import { select } from 'typed-redux-saga';

import { getFeatureABCreateFolderNewFlow } from '../features/features.selectors';
import { waitForFolderBeingLoaded } from '../home/home.saga';
import { createSharedAutoDeleteStart } from '../sharedAutoDelete/sharedAutoDelete.module';
import { isModifyingInProgress } from './modifying.selectors';
import { handeEditPdfCopy, handleCopyItems } from './sagas/copy.saga';
import {
    createFolderRequest,
    createNewFolder,
    handleCreateFamilyAlbum,
    handleCreateFamilyFolder,
    handleCreateFamilyFolderSuccess,
    handleCreateFolderSuccess,
    handleCreateNewFolder,
} from './sagas/createFolder.saga';
import { handleDownloadMobile } from './sagas/download.saga';
import { handleAddToFavoritesItems, handleRemoveFromFavoritesItems } from './sagas/favorites.saga';
import { handleMountRequest, handleUnmountRequest } from './sagas/mount.saga';
import { handleMoveItems } from './sagas/move.saga';
import { handleRemoveItems, handleSilentDeleteFamilyFolder } from './sagas/remove.saga';
import { handleRenameFile } from './sagas/rename.saga';

const IS_PUBLIC = config.get('PUBLIC');

// CLOUDWEB-4301 & CLOUDWEB-4465:
const visibly = new Visibly();
const CHECK_REVISIONS_THROTTLE_TIME = 15000;
const UPDATE_TIME_PRIVATE = 2 * 60 * 1000;
const UPDATE_TIME_PUBLIC = 10 * 60 * 1000;

visibly.init({
    updateTimeMs: IS_PUBLIC ? UPDATE_TIME_PUBLIC : UPDATE_TIME_PRIVATE,
    onVisibleTimer: (id) => {
        const state = store.getState();
        const currentRouteId = getCurrentRouteId(state);
        store.dispatch(checkFolderForUpdate(id ?? currentRouteId));
    },
});

function* handleMakeNewDocumentSuccess() {
    try {
        const storageHelper = getStorage(yield* select(getCurrentStorage));

        if (storageHelper.isStart) {
            yield put(digestRequest());
        }
    } catch (error) {
        yield cancel();
    }
}

function* checkFolderById(folderId: string | undefined, storage: EStorageType) {
    const { isPublic, isStart } = getStorage(storage);

    if (IS_PUBLIC_ALBUM) {
        return;
    }

    let currentFolder: null | HomeFolder | PublicFolder = null;
    if (folderId && !isPublic) {
        const item = yield* select(getHomeItemById, folderId);
        if (item?.isFolder) {
            currentFolder = item;
        }
    } else if (folderId && isPublic) {
        const item = yield* select(getPublicItemById, folderId);
        if (item?.isFolder) {
            currentFolder = item;
        }
    } else {
        const item = yield* select(getHomeItemById, ROOT_FOLDER_ID);
        if (item && 'isFolder' in item && item.isFolder) {
            currentFolder = item as HomeFolder;
        }
    }

    if (!currentFolder) {
        return;
    }

    const oldRev = currentFolder.rev;
    const params = getFolderParams(currentFolder, isPublic);

    if (!params) {
        return;
    }

    const newItem = yield getFileInfoApiCall(params);
    // значение rev в ответе v4 соответствует значению grev в ответе v2
    // grev это то, что для файла приходит как rev
    // а rev для папки - это lrev - ревизия которая меняется при изменении листинга папки
    // rev для файла - это ревизия, которая меняется при изменении файла
    // rev для папки - это рекурсивная ревизия, которая меняется при изменении содержания папки
    // Итого: В хомяке и паблике уже новое апи v4, мы сохранили его rev (которое grev в старом),
    // потому тут из старого для сравнения берем сначала grev
    const newRev = newItem.grev || newItem.rev;

    if (newRev && oldRev === newRev) {
        return;
    }

    if (isStart) {
        yield put(digestRequest());
    } else if (isPublic) {
        yield put(updateFolderRequest({ id: newItem.weblink }));
    } else {
        yield put(loadHomeFolderRequest({ id: params.home, isFolder: true, force: true }));
    }
}

function* handleCheckFolderForUpdate(action: ReturnType<typeof checkFolderForUpdate>) {
    const folderId = action.payload;
    const isModifyingProgress = yield* select(isModifyingInProgress);
    const storage = yield* select(getCurrentStorage);

    if (isModifyingProgress) {
        return;
    }

    try {
        yield checkFolderById(folderId, storage);

        if (folderId !== ROOT_FOLDER_ID) {
            yield checkFolderById(ROOT_FOLDER_ID, EStorageType.home);
        }
    } catch (error) {
        yield cancel();
    }
}

function* handleCreateNewAutoDeleteFolder() {
    try {
        const createChannel = channel();

        yield createNewFolder({
            storage: EStorageType.home,
            parentFolderId: ROOT_FOLDER_ID,
            onCreateFolder: (folderName) => createChannel.put(folderName),
            onClose: () => createChannel.close(),
        });

        const folderName = yield take(createChannel);
        const item = yield* select(getHomeItemById, folderName);

        if (!item) {
            return;
        }

        createChannel.close();

        sendGa('folderCreate', 'autodelete');
        yield put(createSharedAutoDeleteStart(item));
        yield put(publishAutoDeleteFolder({ item }));
    } catch (error) {
        yield cancel();
    }
}

function* handleCreateNewFolderAndPublish(action: PayloadAction<ICreateNewFolderAndPublish>) {
    const { tooltipId } = action.payload || {};

    try {
        yield call(createFolderRequest, {
            folderName: NEW_FOLDER_DEFAULT_NAME,
            share: true,
            parent: ROOT_FOLDER_ID,
            storage: EStorageType.home,
            gaPrefix: 'create-publish',
            ...(tooltipId && { publishPromoTooltip: tooltipId }),
        });
    } catch (error) {
        logger.error(error);
        yield cancel();
    }
}

function* handlePublishAutoDeleteFolder(action: ReturnType<typeof publishAutoDeleteFolder>) {
    const { item } = action.payload;

    if (!item) {
        return;
    }

    yield openPublishDialogSaga({
        item,
        itemStorage: EStorageType.home,
        gaSuffix: 'folderCreate',
        from: EFrom.WEBLINK,
    });

    const isNewFlowEnabled = yield select(getFeatureABCreateFolderNewFlow);
    const itemWithWeblink = yield* select(getHomeItemById, item.id);
    const expires = addYears(new Date(), 1).valueOf();

    yield put(updateWeblinkAutoDeleteRequest({ item: itemWithWeblink as any, expires }));
    // https://jira.vk.team/browse/CLOUDWEB-17980 автооткрытие папки
    if (itemWithWeblink && isNewFlowEnabled) {
        yield call(waitForFolderBeingLoaded, itemWithWeblink.id);
        sendPaymentGa({
            eventCategory: ECategoryGa.folder,
            action: 'open-folder',
            type_folder: 'folder',
            source: 'auto',
        });
    }
}

export function* handlePublish({ cloudPath, name, type, publish }: { cloudPath: string; name: string; type: TItem; publish: boolean }) {
    // может быть загружено несколько одинаковых файлов, нужно обновить паблик у всех
    let inputFiles = ((yield* select(getSuccessInputFiles, { cloudPath })) as IInputFile[]) || [];

    if (type === 'folder') {
        const folder = (yield* select(getFolder, { localPath: `/${name}` })) as IInputFile;

        if (folder) {
            inputFiles = [folder];
        }
    }

    if (!inputFiles?.length) {
        return;
    }

    let i = 0;
    while (i < inputFiles.length) {
        yield put(
            updateUploadFilesAction({
                cloudPath: inputFiles[i].cloudPath,
                descriptorId: inputFiles[i].descriptorId,
                publish,
                extension: inputFiles[i].extension,
                folderCloudPath: inputFiles[i].folderCloudPath,
                localPath: inputFiles[i].localPath,
                name: inputFiles[i].name,
            })
        );
        i++;
    }
}

function* publish(action) {
    const { payload } = action;

    yield call(handlePublish, { cloudPath: payload.id, name: payload.name, type: payload.type, publish: true });
}

function* unPublish(action) {
    const { payload } = action;

    yield call(handlePublish, { cloudPath: payload.id, name: payload.name, type: payload.type, publish: false });
}

function* handleModifyingStop(action) {
    const { reason, storage: storageTemp, id: idTemp } = action.payload ?? {};

    if (!reason) {
        return;
    }

    if (reason === EModifyReason.move || reason === EModifyReason.remove) {
        const id = idTemp ? idTemp : yield* select(getCurrentRouteId);
        const storage = storageTemp ? storageTemp : yield* select(getCurrentStorage);

        yield put(loadMoreRequest({ id, storage, loadOnlyIfLessThanLimit: true }));
    }
}

// eslint-disable-next-line complexity
function* handleLoadMore(action) {
    const { id, storage, loadOnlyIfLessThanLimit } = action.payload;
    const {
        isHome,
        isPublic,
        isTrashBin,
        isSearch,
        isAttaches,
        isFeed,
        isGallery,
        isDocuments,
        isQuotaCleaner,
        isStock,
        isAlbums,
        isAllDocuments,
        isIncomingPublic,
    } = getStorage(storage);

    const folder = yield* select(getStorageCurrentFolder, storage);

    const offset = action.payload.offset ?? path(['count', 'loaded'], folder) ?? 0;

    if (loadOnlyIfLessThanLimit) {
        const apiLimit = getLoadMoreLimit(storage);
        if (offset > apiLimit) {
            return;
        }
    }

    if (isPublic && (yield* select(hasMoreToLoad))) {
        yield put(requestMoreFilesWithFaceStart({}));
        return;
    }

    if (folder && 'hasMoreToLoad' in folder && folder.hasMoreToLoad) {
        if (isHome) {
            yield put(loadMoreHomeRequest({ offset, id }));
        } else if (isPublic) {
            yield put(loadMorePublicFolderRequest({ offset, id }));
        } else if (isSearch) {
            yield put(searchLoadMoreRequest());
        } else if (isTrashBin) {
            yield put(
                trashbinLoadMoreRequest({
                    id,
                    tree: 'tree' in folder ? folder.tree : undefined,
                    nextChunkRev: pathOr(0, ['nextChunkRev'], folder),
                })
            );
        } else if (isAttaches) {
            const params = yield* select(SettingsSelectors.getQueryParams);
            const attachesParams = getAttachesParams({ params }) ?? {};
            yield put(
                attachesLoadMoreRequest({
                    after: pathOr('', ['after'], folder),
                    ...attachesParams,
                })
            );
        } else if (isFeed) {
            yield put(feedLoadMoreRequest());
        } else if (isGallery) {
            yield put(galleryLoadMoreRequest());
        } else if (isDocuments) {
            yield put(loadDocumentsMoreRequest());
        } else if (isAllDocuments) {
            yield put(allDocumentsMoreRequest());
        } else if (isAlbums) {
            yield put(albumsLoadMoreRequest());
        } else if (isQuotaCleaner) {
            yield put(loadUserQuotaGroupMore({ offset }));
        } else if (isStock) {
            yield put(loadStockMoreRequest({ offset, id }));
        } else if (isIncomingPublic) {
            yield put(loadMoreIncomingPublicRequest());
        }
    }
}

function* handleInlineEditSuccess(action: PayloadAction<IInlineEditSuccess>) {
    const { id, place, isFolder } = action.payload;

    if (!isFolder) {
        if (place === EInlineEditPlace.breadcrumbs && !isFolder) {
            yield put(scrollToHomeItemAction({ itemId: id }));
        } else {
            // tempexp-14737-next-line
            yield put(scrollToItemAction(id));
        }
    }

    sendXray(['rename', 'inline', place]);
}

function* handleInlineEditFailure(action: PayloadAction<string | null>) {
    const error = action?.payload;
    if (error) {
        yield put(
            showSnackbarAction({
                id: 'rename-breadcrumb-fail',
                closable: true,
                text: error,
                type: SnackbarTypes.failure,
            })
        );
    }
}

function* UpdateFoldersSaga({ payload }: ReturnType<typeof uploadFilesEnded | typeof removeFileEnded>) {
    const { ids } = payload;
    const rootWeblink = yield* select(getPublicRootWeblink);

    const foldersToUpdate = new Set<string>();

    ids.forEach((id) => {
        const pathParts = id.split('/');

        let acc = pathParts[0];

        for (let i = 1; i < pathParts.length; i++) {
            const part = pathParts[i];

            acc += `/${part}`;

            if (acc === id || (rootWeblink?.weblink && rootWeblink.weblink.includes(acc) && rootWeblink.weblink !== acc)) {
                return;
            }

            foldersToUpdate.add(acc);
        }
    });

    const arrFoldersToUpdate = Array.from(foldersToUpdate);

    for (const folderId of arrFoldersToUpdate) {
        yield put(forceCheckFolderForUpdate(folderId));
    }
}

export function* updatePublicFlagsSaga(action: PayloadAction<IUpdatePublicFlags>) {
    const { weblink, flags } = action.payload;

    try {
        yield weblinksSetFlagsV4ApiCall({ weblink, flags });
    } catch (error) {
        logger.error(error);
        yield cancel();
    }
}

export function* weblinkSetDomainAccessSaga(action: PayloadAction<IWeblinkSetDomainAccess>) {
    const { domain, itemId, storage } = action.payload;

    const item = yield select((state) => getStorageItemById(state, EStorageType[storage], itemId));

    if (!item) {
        yield cancel();
    }

    switch (domain) {
        case DomainAccess.DOMESTIC:
            yield put(updatePublicFlags({ weblink: item.weblink, flags: { DOMESTIC: true } }));
            yield put(toggleWeblinkDomestic({ id: item.id, domestic: true }));
            break;
        case DomainAccess.EMAIL_LIST_ACCESS:
            yield put(updatePublicFlags({ weblink: item.weblink, flags: { EMAIL_LIST_ACCESS: true } }));
            yield put(toggleWeblinkDomestic({ id: item.id, domestic: false }));
            break;
        case DomainAccess.EXTERNAL:
            yield put(updatePublicFlags({ weblink: item.weblink, flags: { DOMESTIC: false, EMAIL_LIST_ACCESS: false } }));
            yield put(toggleWeblinkDomestic({ id: item.id, domestic: false }));
            break;
    }
}

// tempexp_18057-next-line
function* handleCreateThemedPublicAndOpen() {
    try {
        const homeList = (yield select(getHomeList)) as ReturnType<typeof getHomeList>;
        const firstAvailableWeblink = Object.values(homeList).find((item) => item.isFolder && item.weblink);
        let weblink;
        if (firstAvailableWeblink) {
            weblink = firstAvailableWeblink;
        } else {
            const folder = yield call(createFolderRequest, {
                folderName: NEW_FOLDER_DEFAULT_NAME,
                share: false,
                parent: ROOT_FOLDER_ID,
                storage: EStorageType.home,
                gaPrefix: 'create-themed-public',
            });
            yield publishRequestFn({ item: folder, itemStorage: EStorageType.home, publishFrom: 'create-themed-public' });
            weblink = (yield select(getHomeItemById, folder.id)) as ReturnType<typeof getHomeItemById>;
        }

        if (weblink) {
            const url = new URL(getPublicUrl(weblink));
            url.searchParams.append('themeChooser', 'true');
            appendUtmsToPromoLink(url.searchParams);
            opener(url.toString(), true);
        }
    } catch (error) {
        logger.error(error);
        yield cancel();
    }
}

export function* watchModifying() {
    yield takeEvery(updateWeblinkExpiresRequest.toString(), updateExpires);
    yield takeEvery(deleteWeblinkExpiresRequest.toString(), deleteExpires);
    yield takeEvery(updateWeblinkAutoDeleteRequest.toString(), updateAutoDelete);
    yield takeEvery(toggleWeblinkDomesticRequest.toString(), toggleDomestic);
    yield takeEvery(toggleSeoRequest.toString(), toggleSeo);
    yield takeEvery(toggleWeblinkUploadRequest.toString(), toggleUpload);
    yield takeEvery(toggleWeblinkEditableRequest.toString(), toggleEditable);
    yield takeEvery(toggleWeblinkDownloadableRequest.toString(), toggleDownloadable);
    yield takeEvery(updateWeblinkCountDownloadsRequest.toString(), updateCount);
    yield takeEvery(renameFileRequest.toString(), handleRenameFile);
    yield takeEvery(makeNewDocumentSuccess.toString(), handleMakeNewDocumentSuccess);
    yield takeEvery(createNewAutoDeleteFolder.toString(), handleCreateNewAutoDeleteFolder);
    yield takeEvery(publishAutoDeleteFolder.toString(), handlePublishAutoDeleteFolder);
    yield takeEvery(publishWeblink.toString(), publish);
    yield takeEvery(unPublishWeblink.toString(), unPublish);
    yield takeEvery(copyRequest.toString(), handleCopyItems);
    yield takeEvery(editPdfCopy.toString(), handeEditPdfCopy);
    yield takeEvery(moveRequest.toString(), handleMoveItems);
    yield takeEvery(removeRequest.toString(), handleRemoveItems);
    yield throttle(CHECK_REVISIONS_THROTTLE_TIME, checkFolderForUpdate.toString(), handleCheckFolderForUpdate);
    yield takeEvery(forceCheckFolderForUpdate.toString(), handleCheckFolderForUpdate);
    yield takeEvery(modifyingStop.toString(), handleModifyingStop);
    yield takeEvery(loadMoreRequest.toString(), handleLoadMore);
    yield takeEvery(publishRequest.toString(), handlePublishRequest);
    yield takeEvery(unPublishRequest.toString(), handleUnPublishRequest);
    yield takeEvery(openPublishDialog.toString(), handleOpenPublishDialog);
    yield takeEvery(unmountRequest.toString(), handleUnmountRequest);
    yield takeEvery(mountRequest.toString(), handleMountRequest);
    yield takeEvery(addToFavorites.toString(), handleAddToFavoritesItems);
    yield takeEvery(removeFromFavorites.toString(), handleRemoveFromFavoritesItems);
    yield takeEvery(cloneRequest.toString(), handleCloneItems);
    yield takeEvery(editPdfAttachCopy.toString(), handeClonePdfForEdit);
    yield takeEvery(editPublicCopy.toString(), handeEditPublicCopy);
    yield takeEvery(downloadMobileItem.toString(), handleDownloadMobile);
    yield takeEvery(createFolderAction.toString(), handleCreateNewFolder);
    yield takeEvery(createFamilyFolderAction.toString(), handleCreateFamilyFolder);
    yield takeEvery(createFamilyAlbumAction.toString(), handleCreateFamilyAlbum);
    yield takeEvery(createFolderSuccess.toString(), handleCreateFolderSuccess);
    yield takeEvery(createFamilyFolderSuccess.toString(), handleCreateFamilyFolderSuccess);
    yield takeEvery(createNewItem.toString(), handleCreateNewItem);
    yield takeEvery(createNewFolderAndPublish.toString(), handleCreateNewFolderAndPublish);
    yield takeEvery(inlineEditSuccess.toString(), handleInlineEditSuccess);
    yield takeEvery(inlineEditFailure.toString(), handleInlineEditFailure);
    yield takeLatest(uploadFilesEnded.toString(), UpdateFoldersSaga);
    yield takeLatest(removeFileEnded.toString(), UpdateFoldersSaga);
    yield takeLatest(copyRequestEnded.toString(), UpdateFoldersSaga);
    yield takeEvery(updatePublicFlags.toString(), updatePublicFlagsSaga);
    yield takeEvery(weblinkSetDomainAccess.toString(), weblinkSetDomainAccessSaga);
    // tempexp_18057-next-line
    yield takeEvery(createThemedPublicAndOpen.toString(), handleCreateThemedPublicAndOpen);
    // TODO: CLOUD-4610 временная типа-транзакционность семейных папок
    yield takeEvery(silentlyDeleteFamilyFolderOnFailedCreation.toString(), handleSilentDeleteFamilyFolder);
}
